import { AnyAction } from 'redux';
import {
  AddProduktToAngebotAction,
  AddVertragToAngebote,
  AngebotsId,
  DataForDashboard,
  DataForDashboardAfterLoadAzam,
  GlobalActions,
  isMaklerbetreuerMaklerVttelOrVtgev,
  OwnedAngebotsId,
  PreiseForDashboard,
  ProduktDeleted,
  RbdMaxReceived,
  SelectedZusatzoption,
  SetUnderwriterInfoPayload,
  StructuredRisikoortPayload,
  TreuebonusMaxReceived,
  UpdateLoadedVorvertraege,
  VereinbarungenChanged,
  VertragslaufzeitChanged,
  ZaehlstueckeReceived
} from '@ps20-frontend/shop-integration-redux';
import cloneDeep from 'lodash/cloneDeep';
import {
  App,
  areAppTypesEquivalent,
  BasisAdresse,
  BasisRisikoort,
  FeatureToggles,
  FEVorvertragsnummer,
  formatToFEWirksamkeitsbeginn,
  getAppTitle,
  getSpartenTypeByApp,
  isAppTypeInList,
  isAuslandsrisikoort,
  MeasureType,
  Risikoort,
  TierartEnum,
  UnderwriterMeasure,
  UnfallVorbelegungdaten,
  UploadedFile,
  Vorbelegungdaten,
  Vorvertrag,
} from '@ps20-frontend/frontend-models';
import {
  doesUwContextApplyToSparte,
  findUwContext,
  getAllUwContexts,
  ProductUw,
  selectUwEntries,
  UwContextEntry,
  UwContextKey,
  UwEvalContext,
  UwSharedDecision,
} from '@ps20-frontend/uw';
import { Person, Produktlinienname, SofortschutzAvailableItem } from '@ps20/bff-models';
import { sortByCustomAppOrder } from '@ps20-frontend/reducers';
import { convertDateFormatFromShortIsoToLocalState, DATEFORMAT_SHORT_ISO, printPerson } from '@ps20-frontend/utils';
import dayjs from 'dayjs';
import { IAppState } from '../appState';
import { angebotIsTarifiert } from '../selectors/warenkorb.selector';
import { isOwnedByTgv } from '../selectors/tiergruppeWarenkorb.selector';
import { getProduktauswahlContentItem } from '../selectors/produktauswahl.selector';
import { DashboardActions } from '../dashboard.actions';
import {
  UwDecisionUnderwriterInfoBatchItem,
  UwDecisionUnderwriterInfoChangedPayload,
  UnderwriterIdentityField,
  UwEntryInitializePayload,
  UwEntryVertragZeichnenChangedPayload,
  UwEntryFilesUpdatedPayload,
  UwEntryTextsChangedPayload,
  UwDecisionSubmittedItem,
  ProductUwSnapshot,
} from '../model/uwDecisionPayloads';
import { findVorvertrageForAngebot, getVorbelegungdaten } from './helper-functions/warenkorbAngebotHelper';

export interface WarenkorbAngebot extends AngebotsId {
  kurztext?: string;
  uebersichtTitle: string;
  dashboardIcon: string;
  produktKdvId: string;
  ausgewaehlteLinie?: Produktlinienname;
  versichertesRisiko?: string;
  versichertePerson?: Person;
  versicherterRisikoort?: BasisAdresse;
  bruttopreis?: number;
  bruttopreisOhneKombiRabatt?: number;
  laufzeit?: number;
  rbd?: number;
  rbd_max?: number;
  treuebonus?: number;
  rabattstufe: number;
  isZaehlstueck: boolean;
  selectedZusatzoptionen: SelectedZusatzoption[];
  sofortschutzAvailable: boolean;
  vorvertragsNummern?: FEVorvertragsnummer[];
  vorvertragForVorbelegung?: FEVorvertragsnummer;
  tierart?: TierartEnum;

  // Wirksamkeitsbeginn wird nur für beibehaltene Vorvertraege gesetzt
  wirksamkeitsbeginn?: string;
  shouldDisableBeginndatum?: boolean;

  beibehalten: boolean;
  risikoortForElementar?: Risikoort;
  beginn?: string;
  beitragsfreiBeibehalten?: boolean;
  angebotsdatum?: string;
  vorteilsrabatt?: number;
  hasVorschaeden?: boolean;
  nebengebaeudeUeber200?: boolean;
  extremwetterRisk?: boolean;
  uw?: ProductUw;
  coverageType?: string;
  ownerAppId?: App;
  tierSubline?: string;
  tgvPlaceholder?: boolean;
}

function addProductToWarenkorb(
  { appId, angebotsId, angebotsdatum, ownerAppId }: OwnedAngebotsId,
  sectionState: Array<WarenkorbAngebot>,
): WarenkorbAngebot[] {
  const { icon, produktKdvId } = getProduktauswahlContentItem(appId);
  const uebersichtTitle = getAppTitle(appId);
  const unsortedAngebote: Array<WarenkorbAngebot> = [
    ...sectionState,
    {
      appId,
      angebotsId,
      angebotsdatum,
      uebersichtTitle,
      produktKdvId,
      dashboardIcon: icon,
      rabattstufe: 0,
      isZaehlstueck: false,
      selectedZusatzoptionen: [],
      sofortschutzAvailable: false,
      beibehalten: false,
      ...(ownerAppId ? { ownerAppId } : {}),
    },
  ];
  const appOrder: Array<App> = [...new Set(unsortedAngebote.map(angebot => angebot.appId))];
  return sortByCustomAppOrder(appOrder, unsortedAngebote);
}

function setTgvPlaceholder(
  warenkorb: Array<WarenkorbAngebot>,
  tgvPlaceholder: boolean,
): Array<WarenkorbAngebot> {
  const isChanged = (row: WarenkorbAngebot): boolean =>
    row.appId === App.TGV && (row.tgvPlaceholder ?? false) !== tgvPlaceholder;
  if (!warenkorb.some(isChanged)) {
    return warenkorb;
  }
  return warenkorb.map(row => (isChanged(row) ? { ...row, tgvPlaceholder } : row));
}

export const produktReducer = function (
  sectionState: Array<WarenkorbAngebot> = null,
  action: AnyAction,
  state: IAppState,
): Array<WarenkorbAngebot> {
  switch (action.type) {
    case GlobalActions.ADD_PRODUKT_TO_ANGEBOTE: {
      const addProduktAction: AddProduktToAngebotAction = action;
      return addProductToWarenkorb(addProduktAction.payload, sectionState);
    }
    case GlobalActions.VORVERTRAG_BEIBEHALTEN: {
      const vertrag: Vorvertrag = action.payload;
      return sectionState.map((warenkorbAngebot: WarenkorbAngebot) => {
        const matches =
          warenkorbAngebot.beibehalten &&
          vertrag.produktIds.includes(warenkorbAngebot.appId) &&
          warenkorbAngebot.vorvertragsNummern.some(
            vorvertragsNummer =>
              vorvertragsNummer.vertragsnummer === vertrag.vertragsnummer &&
              vorvertragsNummer.spLaufnummer === vertrag.spLaufnummer,
          );

        if (!matches) {
          return warenkorbAngebot;
        }

        if (vertrag.produktIds.includes(App.UNF)) {
          const { vorname, nachname, geburtsdatum } = vertrag
            .vorbelegungdaten[0] as UnfallVorbelegungdaten;
          return {
            ...warenkorbAngebot,
            versichertesRisiko: printPerson(
              vorname,
              nachname,
              convertDateFormatFromShortIsoToLocalState(geburtsdatum),
            ),
          };
        }

        const risikoort: BasisRisikoort = getBasisRisikoort(vertrag);
        if (risikoort) {
          return {
            ...warenkorbAngebot,
            versicherterRisikoort: mapBasisRisikoortToBasisAdresse(risikoort),
            versichertesRisiko: mapBasisRisikoortToRisikoString(risikoort),
          };
        }

        return warenkorbAngebot;
      });
    }
    case GlobalActions.ADD_VORVERTRAG_TO_ANGEBOTE: {
      const {
        appId,
        angebotsId,
        vorvertragsNummern,
        vorvertragSofortschutz,
        beibehalten,
        linieOfVorvertrag,
        bruttoPreis,
        risikoortForElementar,
        vorvertragForVorbelegung,
        angebotsdatum,
        coverageType,
        ownerAppId,
      }: AddVertragToAngebote = action.payload;
      const warenkorbWithAdded = addProductToWarenkorb(
        { angebotsId, appId, angebotsdatum, ownerAppId },
        sectionState,
      );
      vorvertragsNummern.forEach(vv => {
        vv.isVertreterWechsel = state.weitereVertraege?.some(
          wvv => wvv.vertragsnummer === vv.vertragsnummer,
        );
      });
      return warenkorbWithAdded.map((elem: WarenkorbAngebot): WarenkorbAngebot => {
        if (elem.appId === appId && elem.angebotsId === angebotsId) {
          return {
            ...elem,
            angebotsId,
            vorvertragsNummern,
            ausgewaehlteLinie: beibehalten ? linieOfVorvertrag : null,
            bruttopreis: beibehalten ? bruttoPreis : undefined,
            sofortschutzAvailable: vorvertragSofortschutz,
            beibehalten: beibehalten,
            risikoortForElementar,
            vorvertragForVorbelegung,
            coverageType: beibehalten ? coverageType : undefined,
          };
        }
        return elem;
      });
    }
    case GlobalActions.UPDATE_VORVERTRAEGE: {
      const actionPayload: UpdateLoadedVorvertraege = action.payload;
      const beibehalteneVorvertraege: Vorvertrag[] = actionPayload.standardVorvertraege.filter(
        vorvertag => vorvertag.beibehaltung,
      );

      if (beibehalteneVorvertraege.length > 0) {
        return sectionState.map(angebot => {
          const angebotHasVorvertrag =
            angebot?.vorvertragsNummern && angebot.vorvertragsNummern.length > 0;
          if (angebotHasVorvertrag && angebot.beibehalten) {
            // Beibehaltene Angebote haben nur ein Vorvertrag
            const vorvertragForAngebot: Vorvertrag = findVorvertrageForAngebot(
              angebot,
              beibehalteneVorvertraege,
            )[0];
            const vorbelegungdatenForAngebot = getVorbelegungdaten(
              { appId: angebot.appId, angebotsId: angebot.angebotsId },
              vorvertragForAngebot,
            );

            if (vorbelegungdatenForAngebot?.wirksamkeitsbeginn) {
              angebot.wirksamkeitsbeginn = formatToFEWirksamkeitsbeginn(
                vorbelegungdatenForAngebot.wirksamkeitsbeginn,
              );
            }
          }
          return angebot;
        });
      }
      return sectionState;
    }
    case GlobalActions.VORBELEGUNG_FOR_BEIBEHALTUNG_REQUESTED: {
      const actionPayload: Vorbelegungdaten = action.payload;
      return sectionState.map((elem: WarenkorbAngebot): WarenkorbAngebot => {
        if (elem.appId === actionPayload.forApp && elem.angebotsId === actionPayload.angebotsId) {
          return {
            ...elem,
            laufzeit: actionPayload.laufzeit,
            kurztext: actionPayload.verkaufsangebotKurzname,
            rbd: actionPayload.nachlassRbd,
            treuebonus: actionPayload.nachlassTb,
            vorteilsrabatt: actionPayload.vorteilsrabatt,
            wirksamkeitsbeginn: formatToFEWirksamkeitsbeginn(actionPayload.wirksamkeitsbeginn),
            shouldDisableBeginndatum: actionPayload.shouldDisableBeginndatum && elem.beibehalten,
            beitragsfreiBeibehalten: !!actionPayload.beitragsfreiBeibehalten,
          };
        }
        return elem;
      });
    }
    case GlobalActions.PRODUKT_DELETED: {
      const { angebotsIds }: ProduktDeleted = action.payload;
      const remaining = sectionState.filter(
        (elem: WarenkorbAngebot) =>
          !angebotsIds.some(
            angebot => angebot.appId === elem.appId && angebot.angebotsId === elem.angebotsId,
          ),
      );
      const hasOwnedChild = remaining.some(isOwnedByTgv);
      return hasOwnedChild ? remaining : setTgvPlaceholder(remaining, false);
    }
    case DashboardActions.TGV_PLACEHOLDER_ADDED:
      return setTgvPlaceholder(sectionState, true);
    case DashboardActions.TGV_PLACEHOLDER_REMOVED:
    case GlobalActions.TIERGRUPPE_UEBERNOMMEN:
      return setTgvPlaceholder(sectionState, false);
    case GlobalActions.IN_DASHBOARD_UEBERNEHMEN: {
      const {
        appId,
        angebotsId,
        kurztext,
        ausgewaehlteLinie,
        versichertesRisiko,
        tierSubline,
        versichertePerson,
        versicherterRisikoort,
        vertragslaufzeit,
        selectZusatzoptionen,
        tierart,
        beginn,
        hasVorschaeden,
        nebengebaeudeUeber200,
        extremwetterRisk,
        coverageType,
      }: DataForDashboard = action.payload;

      return sectionState.map((elem: WarenkorbAngebot): WarenkorbAngebot => {
        if (elem.appId !== appId || elem.angebotsId !== angebotsId) {
          return elem;
        }

        let rbd = elem.rbd;
        let treuebonus = elem.treuebonus;
        let rbd_max = elem.rbd_max;

        if (isAuslandsrisikoort(versicherterRisikoort)) {
          rbd = 0;
          treuebonus = 0;
          rbd_max = 0;
        }

        const updatedElem: WarenkorbAngebot = {
          ...elem,
          kurztext,
          ausgewaehlteLinie,
          versichertesRisiko,
          tierSubline: tierSubline ?? elem.tierSubline,
          versichertePerson,
          versicherterRisikoort,
          laufzeit: vertragslaufzeit,
          selectedZusatzoptionen: selectZusatzoptionen,
          tierart,
          beginn,
          rbd,
          treuebonus,
          rbd_max,
          hasVorschaeden,
          nebengebaeudeUeber200,
          extremwetterRisk,
          coverageType,
        };

        if (updatedElem.uw) {
          updatedElem.uw = {
            ...updatedElem.uw,
            entries: updatedElem.uw.entries.map(entry => {
              const descriptor = entry.isRequired ? findUwContext(entry.contextKey) : undefined;
              return descriptor ? { ...entry, reason: descriptor.buildReason(updatedElem) } : entry;
            }),
          };
        }

        return updatedElem;
      });
    }
    case GlobalActions.UPDATE_DASHBOARD_AFTER_LOAD_AZAM: {
      const {
        appId,
        angebotsId,
        versichertesRisiko,
        versicherterRisikoort,
        tierart,
      }: DataForDashboardAfterLoadAzam = action.payload;
      return sectionState.map((elem: WarenkorbAngebot): WarenkorbAngebot => {
        if (elem.appId !== appId || elem.angebotsId !== angebotsId) {
          return elem;
        }
        return {
          ...elem,
          versichertesRisiko: versichertesRisiko ?? elem.versichertesRisiko,
          versicherterRisikoort: versicherterRisikoort ?? elem.versicherterRisikoort,
          tierart: tierart ?? elem.tierart,
        };
      });
    }
    case GlobalActions.PREISE_RESET_DASHBOARD:
    case GlobalActions.PREISE_INS_DASHBOARD_UEBERNEHMEN: {
      const { appId, angebotsId, bruttopreis, bruttopreisOhneKombiRabatt }: PreiseForDashboard =
        action.payload;
      return sectionState.map((elem: WarenkorbAngebot): WarenkorbAngebot => {
        if (elem.appId !== appId || elem.angebotsId !== angebotsId) {
          return elem;
        }
        let ausgewaehlteLinie = elem.ausgewaehlteLinie;
        if (action.type === GlobalActions.PREISE_RESET_DASHBOARD) {
          ausgewaehlteLinie = null;
        }
        return {
          ...elem,
          ausgewaehlteLinie,
          bruttopreis,
          bruttopreisOhneKombiRabatt,
        };
      });
    }
    case GlobalActions.VEREINBARUNGEN_CHANGED: {
      return sectionState.map((elem: WarenkorbAngebot): WarenkorbAngebot => {
        const newProduct: WarenkorbAngebot = cloneDeep(elem);
        const actionPayload: VereinbarungenChanged = action.payload;
        const productRBD = actionPayload.rbdChanges.find(
          el => el.appId === newProduct.appId && el.angebotsId === newProduct.angebotsId,
        );
        const productTB = actionPayload.tbChanges.find(
          el => el.appId === newProduct.appId && el.angebotsId === newProduct.angebotsId,
        );
        const productVorteilsrabatt = actionPayload.vorteilsRabattArray.find(
          el => el.appId === newProduct.appId && el.angebotsId === newProduct.angebotsId,
        );
        if (productRBD != null) {
          newProduct.rbd = productRBD.rbd;
        }
        if (productTB != null) {
          newProduct.treuebonus = productTB.treuebonus;
        }
        if (productVorteilsrabatt != null) {
          newProduct.vorteilsrabatt = productVorteilsrabatt.rabattInProzent;
        }
        return newProduct;
      });
    }
    case GlobalActions.RBD_MAX_RECEIVED: {
      const actionPayload: RbdMaxReceived = action.payload;
      return sectionState.map(produkt => {
        if (
          actionPayload.appId === produkt.appId &&
          actionPayload.angebotsId === produkt.angebotsId
        ) {
          return {
            ...produkt,
            rbd: isMaklerbetreuerMaklerVttelOrVtgev(state.transientState.role)
              ? actionPayload.rbdMax
              : 0,
            rbd_max: actionPayload.rbdMax,
          };
        }
        return produkt;
      });
    }
    case GlobalActions.TREUEBONUS_MAX_RECEIVED: {
      const actionPayload: TreuebonusMaxReceived = action.payload;
      return sectionState.map((elem: WarenkorbAngebot): WarenkorbAngebot => {
        if (actionPayload.appId === elem.appId && actionPayload.angebotsId === elem.angebotsId) {
          return {
            ...elem,
            treuebonus: 0,
          };
        }
        return elem;
      });
    }
    case GlobalActions.VERTRAGSLAUFZEIT_CHANGED: {
      return sectionState.map((elem: WarenkorbAngebot): WarenkorbAngebot => {
        const newProduct: WarenkorbAngebot = cloneDeep(elem);
        const actionPayload: VertragslaufzeitChanged = action.payload;
        if (
          newProduct.appId === actionPayload.appId &&
          newProduct.angebotsId === actionPayload.angebotsId
        ) {
          newProduct.laufzeit = actionPayload.vertragslaufzeit;
          if (actionPayload.vertragslaufzeit === 1) {
            newProduct.treuebonus = 0;
          }
        }
        return newProduct;
      });
    }
    case GlobalActions.ZAEHLSTUECKE_RECEIVED: {
      const newState: Array<WarenkorbAngebot> = cloneDeep(sectionState);
      const actionPayload: ZaehlstueckeReceived = action.payload;
      actionPayload.zaehlstuecke.forEach(zaehlstueck => {
        const zaehlstueckProdukt = newState.find(
          produkt =>
            produkt.appId === zaehlstueck.appId && produkt.angebotsId === zaehlstueck.angebotsId,
        );
        if (zaehlstueckProdukt) {
          zaehlstueckProdukt.rabattstufe = zaehlstueck.rabattstufe;
          zaehlstueckProdukt.isZaehlstueck = zaehlstueck.isZaehlstueck;
        }
      });
      return newState;
    }
    case GlobalActions.SOFORTSCHUTZ_AVAILABLE: {
      const actionPayload: SofortschutzAvailableItem[] = action.payload;
      const newState: Array<WarenkorbAngebot> = cloneDeep(sectionState);
      return newState.map(angebotInWarenkorb => {
        if (angebotIsTarifiert(angebotInWarenkorb)) {
          angebotInWarenkorb.sofortschutzAvailable = actionPayload.find(
            item =>
              item.spartenType === getSpartenTypeByApp(angebotInWarenkorb.appId) &&
              (!item.angebotsId || item.angebotsId === angebotInWarenkorb.angebotsId),
          )?.isAvailable;
        }
        return angebotInWarenkorb;
      });
    }
    case GlobalActions.STRUCTURED_RISIKOORT_PROVIDED: {
      const actionPayload: StructuredRisikoortPayload = action.payload;
      const newState: Array<WarenkorbAngebot> = cloneDeep(sectionState);
      const produkt = newState.find(
        angebot =>
          angebot.appId === actionPayload.appId && angebot.angebotsId === actionPayload.angebotsId,
      );
      if (produkt) {
        produkt.versicherterRisikoort = actionPayload.risikoort;
      }
      return newState;
    }
    case GlobalActions.UPDATE_ASSEKURANZTARIF_DATA: {
      if (action.payload) {
        return sectionState.map(produkt => ({
          ...produkt,
          treuebonus: 0,
          rbd: 0,
        }));
      }
      return sectionState;
    }
    case GlobalActions.SET_UNDERWRITER_INFO: {
      if (!action.payload) {
        return sectionState;
      }
      const { angebotsId, requiredByContext, promoteOnly }: SetUnderwriterInfoPayload =
        action.payload;
      const newSectionState = cloneDeep(sectionState);
      const angebot = newSectionState.find(elem => elem.angebotsId === angebotsId);
      if (!angebot) {
        return sectionState;
      }
      const transmittedRequiredByContext: Partial<Record<UwContextKey, boolean>> =
        requiredByContext ?? {};
      const evalContext: UwEvalContext = {
        featureToggles: state?.transientState?.featureToggles ?? ({} as FeatureToggles),
        selectedZusatzoptionen: angebot.selectedZusatzoptionen ?? [],
        isNeugeschaeft: !angebot.beibehalten,
      };
      const spartenType = getSpartenTypeByApp(angebot.appId);
      getAllUwContexts().forEach(descriptor => {
        const isRequired =
          doesUwContextApplyToSparte(descriptor, spartenType) &&
          (descriptor.key in transmittedRequiredByContext
            ? Boolean(transmittedRequiredByContext[descriptor.key])
            : descriptor.requiredWhen?.(angebot, evalContext) ?? false);
        reconcileUwEntryRequired(angebot, descriptor.key, isRequired, promoteOnly);
      });
      discardSharedDecisionWhenNoneSurvives(angebot);
      return newSectionState;
    }
    case GlobalActions.UW_RESUBMISSION_FULFILLED_ANGEBOTE_COLLECTED: {
      if (!action.payload) {
        return sectionState;
      }

      const {
        angebotsIds,
        triggeringAppId,
        triggeringAngebotsId,
        requiredByContext,
      }: {
        angebotsIds: Array<string>;
        triggeringAppId?: App;
        triggeringAngebotsId?: string;
        requiredByContext?: Partial<Record<UwContextKey, boolean>>;
      } = action.payload;

      if (!angebotsIds || angebotsIds.length === 0) {
        return sectionState;
      }

      return sectionState.map(warenkorbAngebot => {
        if (
          !angebotsIds.includes(warenkorbAngebot.angebotsId) ||
          !warenkorbAngebot.uw ||
          warenkorbAngebot.uw.entries.length === 0
        ) {
          return warenkorbAngebot;
        }

        const isTriggeringVwgV2Angebot =
          warenkorbAngebot.angebotsId === triggeringAngebotsId &&
          warenkorbAngebot.appId === App.VWG_V2 &&
          triggeringAppId === App.VWG_V2;
        const transmittedRequiredByContext: Partial<Record<UwContextKey, boolean>> =
          requiredByContext ?? {};

        const newAngebot = cloneDeep(warenkorbAngebot);
        const uw: ProductUw = newAngebot.uw;
        uw.shared = createEmptySharedDecision();
        uw.entries = uw.entries.map((entry: UwContextEntry): UwContextEntry => ({
          ...entry,
          willUnderwrite: null,
          isAcceptedByAgent: undefined,
          files: [],
          texts: {},
          reason: '',
          isRequired:
            isTriggeringVwgV2Angebot && entry.contextKey in transmittedRequiredByContext
              ? Boolean(transmittedRequiredByContext[entry.contextKey])
              : entry.isRequired,
        }));
        return newAngebot;
      });
    }
    case DashboardActions.UW_ENTRY_FILES_UPDATED: {
      const newSectionState = cloneDeep(sectionState);
      const payload: UwEntryFilesUpdatedPayload = action.payload;
      const produkt = newSectionState?.find(
        warenkorbAngebot =>
          warenkorbAngebot.appId === payload.appId &&
          warenkorbAngebot.angebotsId === payload.angebotsId,
      );
      if (!produkt) {
        return sectionState;
      }
      const entry = ensureUwEntry(produkt, payload.contextKey, true);
      payload.uploadResults.forEach((result: UploadedFile) => {
        const existingFile = entry.files.find(file => file.fileName === result.fileName);
        if (existingFile) {
          existingFile.uploadId = result.uploadId;
        } else {
          entry.files.push({
            fileName: result.fileName,
            uploadId: result.uploadId,
            uploadedFileSize: result.uploadedFileSize,
          });
        }
      });
      return newSectionState;
    }
    case DashboardActions.UW_ENTRY_TEXTS_CHANGED: {
      const newSectionState = cloneDeep(sectionState);
      const payload: UwEntryTextsChangedPayload = action.payload;
      const produkt = newSectionState.find(
        warenkorbAngebot =>
          warenkorbAngebot.appId === payload.appId &&
          warenkorbAngebot.angebotsId === payload.angebotsId,
      );
      if (produkt) {
        const entry = ensureUwEntry(produkt, payload.contextKey, true);
        entry.texts = { ...entry.texts, ...payload.texts };
      }
      return newSectionState;
    }
    case DashboardActions.FILE_DELETION_SUCCESSFUL:
    case DashboardActions.FILE_DELETION_FAILED: {
      const newSectionState = cloneDeep(sectionState);
      const payload = action.payload;
      const produkt = newSectionState.find(
        warenkorbAngebot =>
          warenkorbAngebot.appId === payload.appId &&
          warenkorbAngebot.angebotsId === payload.angebotsId,
      );
      produkt?.uw?.entries.forEach(entry => {
        entry.files = entry.files.filter(file => file.uploadId !== payload.extractedDocumentId);
      });
      return newSectionState;
    }
    case DashboardActions.REMOVE_NOT_EXISTING_FILES: {
      const payload = action.payload as Array<string>;
      return sectionState.map(produkt => {
        if (!isAppTypeInList(produkt.appId, [App.VWG, App.VHV]) || !produkt.uw) {
          return produkt;
        }
        return {
          ...produkt,
          uw: {
            ...produkt.uw,
            entries: produkt.uw.entries.map(entry => ({
              ...entry,
              files: entry.files.filter(file => payload.includes(file.uploadId)),
            })),
          },
        };
      });
    }
    case DashboardActions.UW_ENTRY_INITIALIZE: {
      const newSectionState = cloneDeep(sectionState);
      const payload: UwEntryInitializePayload = action.payload;
      const produkt = newSectionState.find(
        warenkorbAngebot =>
          warenkorbAngebot.appId === payload.appId &&
          warenkorbAngebot.angebotsId === payload.angebotsId,
      );
      if (produkt) {
        const entry = ensureUwEntry(produkt, payload.contextKey, true);
        if (entry.willUnderwrite === null) {
          entry.reason = payload.grund;
          produkt.uw.shared.risikoAddress = payload.risikoAddress;
          produkt.uw.shared.produktName = payload.produktName;
        }
      }
      return newSectionState;
    }
    case DashboardActions.UW_ENTRY_VERTRAG_ZEICHNEN_CHANGED: {
      const newSectionState = cloneDeep(sectionState);
      const payload: UwEntryVertragZeichnenChangedPayload = action.payload;
      const produkt = newSectionState.find(
        warenkorbAngebot =>
          warenkorbAngebot.appId === payload.appId &&
          warenkorbAngebot.angebotsId === payload.angebotsId,
      );
      const entry = findUwEntry(produkt, payload.contextKey);
      if (entry) {
        entry.willUnderwrite = payload.vertragZeichnen;

        if (payload.vertragZeichnen === false) {
          const otherContextIsApproved = produkt.uw.entries.some(
            other => other.contextKey !== payload.contextKey && other.willUnderwrite === true,
          );
          if (!otherContextIsApproved) {
            produkt.uw.shared.measures = [];
          }
        }
      }
      return newSectionState;
    }
    case DashboardActions.UW_DECISION_MASSNAHME_ADDED: {
      const newSectionState = cloneDeep(sectionState);
      const payload = action.payload;
      const produkt = newSectionState.find(
        warenkorbAngebot =>
          warenkorbAngebot.appId === payload.appId &&
          warenkorbAngebot.angebotsId === payload.angebotsId,
      );
      const shared = produkt?.uw?.shared;
      if (shared) {
        // Initialize fields based on massnahme type (same logic as onMeasureTypeChange in component)
        const newMassnahme: UnderwriterMeasure = {
          type: payload.massnahme,
          zuschlagProzent: payload.massnahme === MeasureType.ZUSCHLAG ? 0 : undefined,
          vereinbarungText:
            payload.massnahme === MeasureType.INDIVIDUELLE_VEREINBARUNG ? '' : undefined,
        };

        shared.measures.push(newMassnahme);
      }
      return newSectionState;
    }
    case DashboardActions.UW_DECISION_MASSNAHME_REMOVED: {
      const newSectionState = cloneDeep(sectionState);
      const payload = action.payload;
      const produkt = newSectionState.find(
        warenkorbAngebot =>
          warenkorbAngebot.appId === payload.appId &&
          warenkorbAngebot.angebotsId === payload.angebotsId,
      );
      const shared = produkt?.uw?.shared;
      if (shared) {
        shared.measures.splice(payload.massnahmeIndex, 1);
      }
      return newSectionState;
    }
    case DashboardActions.UW_DECISION_MASSNAHME_UPDATED: {
      const newSectionState = cloneDeep(sectionState);
      const payload = action.payload;
      const produkt = newSectionState.find(
        warenkorbAngebot =>
          warenkorbAngebot.appId === payload.appId &&
          warenkorbAngebot.angebotsId === payload.angebotsId,
      );
      const shared = produkt?.uw?.shared;
      if (shared) {
        if (shared.measures[payload.massnahmeIndex]) {
          shared.measures[payload.massnahmeIndex] = {
            ...shared.measures[payload.massnahmeIndex],
            ...payload.updates,
          };
        }
      }
      return newSectionState;
    }
    case DashboardActions.UW_DECISION_DOKUMENTATION_CHANGED: {
      const newSectionState = cloneDeep(sectionState);
      const payload = action.payload;
      const produkt = newSectionState.find(
        warenkorbAngebot =>
          warenkorbAngebot.appId === payload.appId &&
          warenkorbAngebot.angebotsId === payload.angebotsId,
      );
      if (produkt?.uw) {
        produkt.uw.shared.documentation = payload.dokumentation;
      }
      return newSectionState;
    }
    case DashboardActions.UW_DECISION_UNDERWRITER_INFO_CHANGED: {
      const newSectionState = cloneDeep(sectionState);
      const payload: UwDecisionUnderwriterInfoChangedPayload = action.payload;
      const produkt = newSectionState.find(
        warenkorbAngebot =>
          warenkorbAngebot.appId === payload.appId &&
          warenkorbAngebot.angebotsId === payload.angebotsId,
      );
      setUnderwriterIdentity(produkt, payload.field, payload.value);
      return newSectionState;
    }
    case DashboardActions.UW_DECISION_UNDERWRITER_INFO_BATCH_CHANGED: {
      const newSectionState = cloneDeep(sectionState);
      const updates = action.payload;
      updates.forEach((update: UwDecisionUnderwriterInfoBatchItem) => {
        const produkt = newSectionState.find(
          warenkorbAngebot =>
            warenkorbAngebot.appId === update.appId &&
            warenkorbAngebot.angebotsId === update.angebotsId,
        );
        setUnderwriterIdentity(produkt, update.field, update.value);
      });
      return newSectionState;
    }
    case DashboardActions.UW_DECISION_SUBMITTED: {
      const newSectionState = cloneDeep(sectionState);
      const decisions: UwDecisionSubmittedItem[] = action.payload;
      const timestamp = dayjs().format(DATEFORMAT_SHORT_ISO);
      decisions.forEach(decision => {
        const produkt = newSectionState.find(
          warenkorbAngebot =>
            warenkorbAngebot.appId === decision.appId &&
            warenkorbAngebot.angebotsId === decision.angebotsId,
        );
        if (produkt?.uw) {
          produkt.uw.shared = {
            ...produkt.uw.shared,
            measures: decision.massnahmen,
            documentation: decision.dokumentation,
            timestamp,
          };
          const entry = ensureUwEntry(produkt, decision.contextKey, true);
          entry.willUnderwrite = decision.vertragZeichnen;
          entry.reason = decision.grund;
          if (decision.isAcceptedByAgent !== undefined) {
            entry.isAcceptedByAgent = decision.isAcceptedByAgent;
          }
        }
      });
      return newSectionState;
    }
    case DashboardActions.UW_ENTRY_SNAPSHOT_RESTORE: {
      const newSectionState = cloneDeep(sectionState);
      const snapshots: ProductUwSnapshot[] = action.payload;
      snapshots.forEach(snap => {
        const produkt = newSectionState.find(
          w => w.appId === snap.appId && w.angebotsId === snap.angebotsId,
        );
        if (!produkt?.uw || !snap.uw) {
          return;
        }
        produkt.uw.shared = cloneDeep(snap.uw.shared);
        const snapshotEntries = snap.uw.entries;

        produkt.uw.entries.forEach(entry => {
          const snapEntry = snapshotEntries.find(candidate => candidate.contextKey === entry.contextKey);
          restoreEntryFromSnapshotEntry(entry, snapEntry);
        });
      });
      return newSectionState;
    }
    case DashboardActions.UW_MASSNAHMEN_ACCEPTED: {
      const newSectionState: WarenkorbAngebot[] = cloneDeep(sectionState);
      newSectionState.forEach(warenkorbAngebot => {
        if (!warenkorbAngebot.uw) {
          return;
        }
        const hasNonKeineMassnahmen =
          warenkorbAngebot.uw.shared.measures?.filter(
            massnahme => massnahme.type !== MeasureType.KEINE_MASSNAHME,
          ).length > 0;

        warenkorbAngebot.uw.entries.forEach(entry => {
          if (entry.willUnderwrite === null) {
            return;
          }
          if (hasNonKeineMassnahmen || entry.willUnderwrite === false) {
            entry.isAcceptedByAgent = true;
          }
        });
      });
      return newSectionState;
    }
    default:
      return sectionState;
  }
};

export function getBasisRisikoort(vorvertrag: Vorvertrag): BasisRisikoort {
  if (vorvertrag.produktIds.some(produktId => areAppTypesEquivalent(produktId, App.VWG) || areAppTypesEquivalent(produktId, App.VHV))) {
    const vorbelegungdaten: Vorbelegungdaten[] = vorvertrag?.vorbelegungdaten?.filter(daten =>
      vorvertrag.produktIds.some(produktId => areAppTypesEquivalent(produktId, daten.forApp))
    );
    return vorbelegungdaten.map(vorbelegung => vorbelegung as unknown as BasisRisikoort)[0];
  }
  return null;
}

function setUnderwriterIdentity(
  produkt: WarenkorbAngebot | undefined,
  field: UnderwriterIdentityField,
  value: string,
): void {
  if (produkt?.uw) {
    produkt.uw.shared[field] = value;
  }
}

function createEmptySharedDecision(): UwSharedDecision {
  return {
    measures: [],
    documentation: '',
    underwriterName: '',
    underwriterBensl: '',
    risikoAddress: '',
    produktName: '',
  };
}

function findUwEntry(
  produkt: WarenkorbAngebot | undefined,
  contextKey: UwContextKey,
): UwContextEntry | undefined {
  return selectUwEntries(produkt?.uw, contextKey)[0];
}

function ensureUwEntry(
  produkt: WarenkorbAngebot,
  contextKey: UwContextKey,
  isRequired: boolean,
): UwContextEntry {
  if (!produkt.uw) {
    produkt.uw = { shared: createEmptySharedDecision(), entries: [] };
  }
  let entry = selectUwEntries(produkt.uw, contextKey)[0];
  if (!entry) {
    entry = {
      contextKey,
      isRequired,
      willUnderwrite: null,
      files: [],
      texts: {},
      reason: '',
    };
    produkt.uw.entries.push(entry);
  }
  return entry;
}

function discardSharedDecisionWhenNoneSurvives(produkt: WarenkorbAngebot): void {
  const entries = produkt.uw?.entries ?? [];
  if (entries.length === 0) {
    return;
  }
  if (entries.some(entry => entry.willUnderwrite !== null)) {
    return;
  }
  produkt.uw.shared = createEmptySharedDecision();
}

function reconcileUwEntryRequired(
  produkt: WarenkorbAngebot,
  contextKey: UwContextKey,
  isRequired: boolean,
  promoteOnly = false,
): void {
  const existingEntry = findUwEntry(produkt, contextKey);
  if (existingEntry) {
    if (promoteOnly && !isRequired) {
      return;
    }
    const isBecomingRequiredAgain = isRequired && !existingEntry.isRequired;
    existingEntry.isRequired = isRequired;
    if (isBecomingRequiredAgain) {
      existingEntry.willUnderwrite = null;
      existingEntry.isAcceptedByAgent = undefined;
      existingEntry.files = [];
      existingEntry.texts = {};
      existingEntry.reason = '';
    }
    return;
  }
  if (isRequired) {
    ensureUwEntry(produkt, contextKey, isRequired);
  }
}

function restoreEntryFromSnapshotEntry(
  entry: UwContextEntry,
  snapEntry: UwContextEntry | undefined,
): void {
  entry.willUnderwrite = snapEntry?.willUnderwrite ?? null;
  entry.isAcceptedByAgent = snapEntry?.isAcceptedByAgent;
  entry.reason = snapEntry?.reason ?? '';
}

function mapBasisRisikoortToBasisAdresse(basisRisikoort: BasisRisikoort): BasisAdresse {
  return {
    ort: basisRisikoort.risikoort?.ort,
    strasse: basisRisikoort.risikoort?.strasse,
    hausnummer: basisRisikoort.risikoort?.hausnummer,
    plz: basisRisikoort.risikoort?.plz,
  };
}

export function mapBasisRisikoortToRisikoString(basisRisikoort: BasisRisikoort): string {
  const risikoort: BasisAdresse = basisRisikoort.risikoort;
  const risikoortString = `${risikoort?.strasse} ${risikoort?.hausnummer}, ${risikoort?.plz} ${risikoort?.ort}, `;
  const wohnflaecheString = `${basisRisikoort.wohnflaeche} m²`;
  return `${risikoortString}${wohnflaecheString}`;
}
